
import React from 'react';
import { startOfDay, parseISO, format, endOfDay, isSameDay } from 'date-fns';
import { Event } from '@/lib/types';
import { isMultiDayEvent, getEventsForDay, isEventInDateRange } from '@/utils/eventUtils';
import { processMultiDayEvents, createDaysWithEventsMap, getMaxVerticalPosition } from '@/utils/calendarEventLayout';
import CalendarDayCell from '@/components/calendar/CalendarDayCell';
import MultiDayEventsLayer from '@/components/calendar/MultiDayEventsLayer';

interface CalendarBodyProps {
  days: Date[];
  daysToShow: number;
  cellWidth: number;
  filteredEvents: Event[];
  onEventClick: (event: Event) => void;
}

const CalendarBody = ({
  days,
  daysToShow,
  cellWidth,
  filteredEvents,
  onEventClick
}: CalendarBodyProps) => {
  // Define the visible date range
  const startDate = startOfDay(days[0]);
  const endDate = endOfDay(days[days.length - 1]);
  const today = new Date();
  
  // Filter events to only include those in the visible date range
  const visibleEvents = filteredEvents.filter(event => 
    isEventInDateRange(event, startDate, endDate)
  );

  // Check if there are any events to display
  const hasAnyEvents = visibleEvents.length > 0;
  
  // Extract multi-day events only if we have events
  const multiDayEvents = hasAnyEvents ? visibleEvents.filter(isMultiDayEvent) : [];
  
  // Process multi-day events to calculate their positions only if we have events
  const processedMultiDayEvents = hasAnyEvents 
    ? processMultiDayEvents(multiDayEvents, days) 
    : [];
  
  // Get all multi-day event IDs to exclude them from single-day display
  const multiDayEventIds = new Set(processedMultiDayEvents.map(e => e.event.id));

  // Calculate the maximum vertical position used by multi-day events
  const maxVerticalPosition = getMaxVerticalPosition(processedMultiDayEvents);
  
  // Create a map of days to events for efficient lookup
  const daysWithEvents = createDaysWithEventsMap(processedMultiDayEvents, days);
  
  return (
    <div className="relative font-sans">
      {/* Day cells flex layout - matched with header */}
      <div 
        className="flex border border-t-0 border-medihub-midPurple/20 rounded-b-lg"
        style={{ minHeight: `${Math.max(180, (maxVerticalPosition + 1) * 32 + 180)}px` }}
      >
        {days.map((day, index) => {
          // Get single-day events for this specific day (exclude multi-day events)
          const dayEvents = hasAnyEvents 
            ? getEventsForDay(visibleEvents, day)
              .filter(event => !multiDayEventIds.has(event.id))
              .filter(event => !isMultiDayEvent(event))
            : [];
          
          // Check if this day has any events
          const dayKey = format(day, 'yyyy-MM-dd');
          const hasSingleDayEvents = dayEvents.length > 0;
          const hasMultiDayEvents = daysWithEvents.get(dayKey) === true;
          const isEmpty = !hasSingleDayEvents && !hasMultiDayEvents;
          const isToday = isSameDay(day, today);

          return (
            <CalendarDayCell
              key={`day-${index}`}
              day={day}
              index={index}
              dayEvents={dayEvents}
              isEmpty={isEmpty}
              isToday={isToday}
              cellWidth={cellWidth}
              maxVerticalPosition={maxVerticalPosition}
              onEventClick={onEventClick}
            />
          );
        })}
      </div>

      {/* Multi-day events layer positioned absolutely over the grid */}
      {hasAnyEvents && (
        <MultiDayEventsLayer 
          processedEvents={processedMultiDayEvents}
          onEventClick={onEventClick}
          cellWidth={cellWidth}
        />
      )}
    </div>
  );
};

export default CalendarBody;
